import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Refresh as RefreshIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { createApiClient, getApiBaseUrl } from '../../services/httpClient';
import useBusinessCategoryOptions from '../../hooks/useBusinessCategoryOptions';
import {
  FRANCHISE_FEATURE_CATALOG,
  getFeatureMeta,
} from '../../config/franchiseeFeatures';

const apiClient = createApiClient(getApiBaseUrl());
const ENDPOINT = '/franchisee/admin/dummy-logins';
const EMPTY_FORM = { phone: '', pin: '', business_name: '', business_category: '' };

const digitsOnly = (value, maxLength) =>
  String(value || '')
    .replace(/\D/g, '')
    .slice(0, maxLength);

const getRowId = (row) => row?.id ?? row?.dummy_login_id ?? row?._id;
const getRowCategory = (row) =>
  row?.business_category ??
  row?.business_type ??
  row?.service_category ??
  row?.category ??
  '';

const getRows = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.dummy_logins)) return data.dummy_logins;
  if (Array.isArray(data?.items)) return data.items;
  return [];
};

const getEnabledFeatureKeys = (row) => {
  if (!Array.isArray(row?.features)) return [];
  return row.features
    .filter((feature) => feature?.is_enabled !== false)
    .map((feature) => String(feature?.feature_name || feature?.key || feature || '').trim())
    .filter(Boolean);
};

const getErrorMessage = (error, fallback) => {
  if (error?.response?.status === 403) {
    return 'You do not have permission to manage testing logins.';
  }
  return error?.response?.data?.message || error?.message || fallback;
};

const validateForm = (form) => {
  if (!/^\d{10}$/.test(form.phone)) return 'Phone must contain exactly 10 digits.';
  if (!/^\d{4,9}$/.test(form.pin)) return 'PIN must contain 4 to 9 digits.';
  if (!form.business_category) return 'Select one business or service category.';
  if (String(form.business_name || '').trim().length > 120) {
    return 'Business name must be 120 characters or fewer.';
  }
  return '';
};

const toPayload = (form) => ({
  phone: form.phone,
  pin: form.pin,
  business_category: form.business_category,
  business_name: String(form.business_name || '').trim(),
});

const TestingLoginsPanel = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY_FORM);
  const [reviewRow, setReviewRow] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureSearch, setFeatureSearch] = useState('');
  const { options: categoryOptions, loading: categoriesLoading } =
    useBusinessCategoryOptions();

  const categoryLabels = useMemo(
    () => new Map(categoryOptions.map((option) => [String(option.value), option.label])),
    [categoryOptions],
  );

  const filteredFeatureRows = useMemo(() => {
    const query = featureSearch.trim().toLowerCase();
    if (!query) return FRANCHISE_FEATURE_CATALOG;
    return FRANCHISE_FEATURE_CATALOG.filter((feature) => {
      const haystack = [feature.key, feature.label, feature.description, ...(feature.keywords || [])]
        .join(' ')
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [featureSearch]);

  const loadLogins = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiClient.get(ENDPOINT);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to load testing logins.');
      }
      setRows(getRows(response.data.data));
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to load testing logins.'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadLogins();
  }, [loadLogins]);

  const setField = (setter, field, value) => {
    const nextValue =
      field === 'phone'
        ? digitsOnly(value, 10)
        : field === 'pin'
          ? digitsOnly(value, 9)
          : field === 'business_name'
            ? String(value || '').slice(0, 120)
            : value;
    setter((current) => ({ ...current, [field]: nextValue }));
  };

  const handleCreate = async (event) => {
    event.preventDefault();
    const validationError = validateForm(form);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const response = await apiClient.post(ENDPOINT, toPayload(form));
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to create testing login.');
      }
      setForm(EMPTY_FORM);
      setSuccess(response.data?.message || 'Testing login created.');
      await loadLogins();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to create testing login.'));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditForm({
      phone: digitsOnly(row?.phone, 10),
      pin: digitsOnly(row?.pin ?? row?.login_pin, 9),
      business_name: String(row?.business_name || '').slice(0, 120),
      business_category: String(getRowCategory(row)),
    });
    setError('');
    setSuccess('');
  };

  const openReview = (row) => {
    setReviewRow(row);
    setSelectedFeatures(getEnabledFeatureKeys(row));
    setFeatureSearch('');
    setError('');
    setSuccess('');
  };

  const toggleFeature = (featureKey) => {
    setSelectedFeatures((current) =>
      current.includes(featureKey)
        ? current.filter((key) => key !== featureKey)
        : [...current, featureKey],
    );
  };

  const handleUpdate = async () => {
    const validationError = validateForm(editForm);
    if (validationError) {
      setError(validationError);
      return;
    }

    const id = getRowId(editRow);
    if (id === undefined || id === null) {
      setError('This testing login has no editable identifier.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await apiClient.patch(
        `${ENDPOINT}/${encodeURIComponent(id)}`,
        toPayload(editForm),
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to update testing login.');
      }
      setEditRow(null);
      setSuccess(response.data?.message || 'Testing login updated.');
      await loadLogins();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to update testing login.'));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFeatures = async () => {
    const id = getRowId(reviewRow);
    if (id === undefined || id === null) {
      setError('This testing login has no editable identifier.');
      return;
    }
    if (!selectedFeatures.length) {
      setError('Select at least one feature before saving.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const response = await apiClient.put(
        `${ENDPOINT}/${encodeURIComponent(id)}/features`,
        { features: selectedFeatures },
      );
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to update features.');
      }
      setReviewRow(null);
      setSuccess(response.data?.message || 'Features updated for testing login.');
      await loadLogins();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to update features.'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    const id = getRowId(row);
    if (id === undefined || id === null) {
      setError('This testing login has no deletable identifier.');
      return;
    }
    const phone = row?.phone || 'this phone number';
    const confirmed = window.confirm(
      `Delete testing login ${phone}? This will permanently remove it and free the number for reuse.`,
    );
    if (!confirmed) return;

    setBusyId(String(id));
    setError('');
    setSuccess('');
    try {
      const response = await apiClient.delete(`${ENDPOINT}/${encodeURIComponent(id)}`);
      if (!response.data?.success) {
        throw new Error(response.data?.message || 'Failed to delete testing login.');
      }
      setSuccess(response.data?.message || 'Testing login deleted and number freed.');
      await loadLogins();
    } catch (requestError) {
      setError(getErrorMessage(requestError, 'Failed to delete testing login.'));
    } finally {
      setBusyId('');
    }
  };

  const categorySelect = (value, onChange, label = 'Business / service category') => (
    <TextField
      select
      required
      fullWidth
      size="small"
      label={label}
      value={value}
      onChange={onChange}
      disabled={categoriesLoading}
    >
      {categoryOptions.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );

  return (
    <CardContent sx={{ px: { xs: 1.5, sm: 2.5 }, py: { xs: 2, sm: 2.5 } }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', sm: 'center' }}
        spacing={1}
        sx={{ mb: 2 }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Dummy Mitra testing logins
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Create limited test credentials, review features, and grant Export Catalog like Active Mitras.
          </Typography>
        </Box>
        <Button
          variant="outlined"
          size="small"
          startIcon={<RefreshIcon />}
          onClick={loadLogins}
          disabled={loading}
        >
          Refresh
        </Button>
      </Stack>

      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper
        component="form"
        onSubmit={handleCreate}
        variant="outlined"
        sx={{ p: { xs: 1.5, sm: 2 }, mb: 3, borderRadius: 2 }}
      >
        <Typography fontWeight={700} sx={{ mb: 1.5 }}>
          Add testing login
        </Typography>
        <Grid container spacing={1.5} alignItems="flex-start">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              required
              fullWidth
              size="small"
              label="Phone"
              value={form.phone}
              onChange={(event) => setField(setForm, 'phone', event.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{10}', maxLength: 10 }}
              helperText="Exactly 10 digits"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              required
              fullWidth
              size="small"
              label="PIN"
              type="text"
              value={form.pin}
              onChange={(event) => setField(setForm, 'pin', event.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{4,9}', maxLength: 9 }}
              helperText="4 to 9 digits"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Business name"
              value={form.business_name}
              onChange={(event) => setField(setForm, 'business_name', event.target.value)}
              inputProps={{ maxLength: 120 }}
              helperText="Optional — auto-filled if blank"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            {categorySelect(form.business_category, (event) =>
              setField(setForm, 'business_category', event.target.value),
            )}
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              type="submit"
              fullWidth
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
              disabled={saving || categoriesLoading}
              sx={{ minHeight: 40 }}
            >
              Create
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
          <CircularProgress />
        </Box>
      ) : rows.length === 0 ? (
        <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <Typography fontWeight={700}>No testing logins yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Create one above when a dummy Mitra account is needed.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {rows.map((row, index) => {
            const id = getRowId(row);
            const category = String(getRowCategory(row));
            const pin = row?.pin ?? row?.login_pin ?? '—';
            const enabledFeatures = getEnabledFeatureKeys(row);
            const hasExport = enabledFeatures.includes('export_catalog');
            return (
              <Grid item xs={12} sm={6} lg={4} key={id ?? `${row.phone}-${index}`}>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    height: '100%',
                    borderRadius: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary">
                        Phone
                      </Typography>
                      <Typography fontWeight={700}>{row.phone || '—'}</Typography>
                    </Box>
                    <Stack direction="row">
                      <Tooltip title="Review / features">
                        <IconButton size="small" onClick={() => openReview(row)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit testing login">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete and free number">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(row)}
                            disabled={busyId === String(id)}
                          >
                            {busyId === String(id) ? (
                              <CircularProgress size={18} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>
                  <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                    <Box sx={{ minWidth: 0, flex: '1 1 140px' }}>
                      <Typography variant="caption" color="text.secondary">
                        Business name
                      </Typography>
                      <Typography variant="body2" fontWeight={700} sx={{ wordBreak: 'break-word' }}>
                        {row.business_name || '—'}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">
                        PIN
                      </Typography>
                      <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>
                        {pin}
                      </Typography>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: '1 1 120px' }}>
                      <Typography variant="caption" color="text.secondary">
                        Category
                      </Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {categoryLabels.get(category) || category || '—'}
                      </Typography>
                    </Box>
                  </Stack>

                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="caption" color="text.secondary">
                      {enabledFeatures.length} feature{enabledFeatures.length === 1 ? '' : 's'}
                    </Typography>
                    {hasExport && (
                      <Chip size="small" color="success" label="Export Catalog" />
                    )}
                  </Stack>

                  {enabledFeatures.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {enabledFeatures.slice(0, 3).map((feature) => (
                        <Chip
                          key={feature}
                          label={getFeatureMeta(feature).label || feature}
                          size="small"
                          variant="outlined"
                        />
                      ))}
                      {enabledFeatures.length > 3 && (
                        <Chip
                          label={`+${enabledFeatures.length - 3} more`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      )}

      <Dialog open={Boolean(editRow)} onClose={() => !saving && setEditRow(null)} fullWidth maxWidth="sm">
        <DialogTitle>Edit testing login</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              required
              fullWidth
              label="Phone"
              value={editForm.phone}
              onChange={(event) => setField(setEditForm, 'phone', event.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{10}', maxLength: 10 }}
              helperText="Exactly 10 digits"
            />
            <TextField
              required
              fullWidth
              label="PIN"
              type="text"
              value={editForm.pin}
              onChange={(event) => setField(setEditForm, 'pin', event.target.value)}
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]{4,9}', maxLength: 9 }}
              helperText="4 to 9 digits"
            />
            <TextField
              fullWidth
              label="Business name"
              value={editForm.business_name}
              onChange={(event) => setField(setEditForm, 'business_name', event.target.value)}
              inputProps={{ maxLength: 120 }}
              helperText="Leave blank to auto-generate from category"
            />
            {categorySelect(editForm.business_category, (event) =>
              setField(setEditForm, 'business_category', event.target.value),
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditRow(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={saving || categoriesLoading}
          >
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(reviewRow)}
        onClose={() => !saving && setReviewRow(null)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Review testing login features</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {reviewRow?.phone || '—'}
              {reviewRow?.business_name ? ` · ${reviewRow.business_name}` : ''}
            </Typography>
            <Alert severity="info">
              Toggle features the same way as Active Mitras. Enable <strong>Export Catalog</strong> to
              test Share / export in the Mitra app.
            </Alert>
            <TextField
              size="small"
              label="Search features"
              value={featureSearch}
              onChange={(event) => setFeatureSearch(event.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              {selectedFeatures.length} selected · {filteredFeatureRows.length}/
              {FRANCHISE_FEATURE_CATALOG.length} shown
            </Typography>
            <Box
              sx={{
                maxHeight: 420,
                overflowY: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                p: 1,
              }}
            >
              <Stack spacing={0.5}>
                {filteredFeatureRows.map((feature) => {
                  const checked = selectedFeatures.includes(feature.key);
                  const isExport = feature.key === 'export_catalog';
                  return (
                    <Paper
                      key={feature.key}
                      variant="outlined"
                      sx={{
                        px: 1.25,
                        py: 0.75,
                        borderColor: isExport && checked ? 'success.main' : 'divider',
                        bgcolor: isExport ? 'rgba(46, 125, 50, 0.04)' : 'background.paper',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={checked}
                            onChange={() => toggleFeature(feature.key)}
                            size="small"
                          />
                        }
                        label={
                          <Box>
                            <Typography fontWeight={700} variant="body2">
                              {feature.label}
                              {isExport ? ' (ops-only)' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {feature.description}
                            </Typography>
                          </Box>
                        }
                        sx={{ alignItems: 'flex-start', m: 0, width: '100%' }}
                      />
                    </Paper>
                  );
                })}
                {filteredFeatureRows.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    No features matched “{featureSearch}”.
                  </Typography>
                )}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReviewRow(null)} disabled={saving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveFeatures} disabled={saving}>
            {saving ? 'Saving…' : 'Save features'}
          </Button>
        </DialogActions>
      </Dialog>
    </CardContent>
  );
};

export default TestingLoginsPanel;
